import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { formatPrice } from "@/utils/currency";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { toast } from "sonner";
import { Search, Trash2, Loader2 } from "lucide-react";

export function InsumosTab() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [insumos, setInsumos] = useState([]); const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(""); const [catFilter, setCatFilter] = useState("__all__");
  const [showForm, setShowForm] = useState(false); const [name, setName] = useState(""); const [code, setCode] = useState("");
  const [category, setCategory] = useState(""); const [quantity, setQuantity] = useState(0); const [costPrice, setCostPrice] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const fetchInsumos = useCallback(async () => {
    try { const res = await authAxiosRef.current.get("/menu/insumos"); setInsumos(res.data); } catch { }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchInsumos(); }, [fetchInsumos]);

  const createInsumo = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await authAxios.post("/menu/insumos", { name, code, category, quantity: parseFloat(quantity), cost_price: parseFloat(costPrice) });
      toast.success("Insumo creado");
      setShowForm(false);
      setName(""); setCode(""); setCategory(""); setQuantity(0); setCostPrice(0);
      fetchInsumos();
    } catch { toast.error("Error"); }
    setSubmitting(false);
  };

  const deleteInsumo = async (id) => {
    try { await authAxios.delete(`/menu/insumos/${id}`); toast.success("Eliminado"); fetchInsumos(); } catch { toast.error("Error"); }
  };

  const categories = [...new Set(insumos.map(i => i.category).filter(Boolean))];
  const filtered = insumos.filter(i => {
    if (catFilter && catFilter !== "__all__" && i.category !== catFilter) return false;
    if (search && !i.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (loading) return <LoadingScreen />;

  return (
    <div data-testid="insumos-tab">
      <div className="flex items-center gap-3 mb-4">
        <Button variant="outline" className="border-emerald-300 text-emerald-700 font-bold uppercase" onClick={() => setShowForm(true)} data-testid="new-insumo-btn">NUEVO INSUMO</Button>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-48 bg-white border-gray-200"><SelectValue placeholder="Categoria" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todas</SelectItem>
            {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Buscar" value={search} onChange={e => setSearch(e.target.value)} className="pl-9 w-64 bg-white border-gray-200" data-testid="search-insumos" />
        </div>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-gray-500 text-xs uppercase">
              <th className="text-left p-3 font-medium">Nombre</th>
              <th className="text-left p-3 font-medium">Codigo</th>
              <th className="text-left p-3 font-medium">Categoria</th>
              <th className="text-center p-3 font-medium">Cantidad</th>
              <th className="text-right p-3 font-medium">Precio de costo</th>
              <th className="p-3 w-16"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.map(insumo => (
              <tr key={insumo.id} className="hover:bg-gray-50" data-testid={`insumo-row-${insumo.id.slice(-6)}`}>
                <td className="p-3 font-medium">{insumo.name}</td>
                <td className="p-3 text-gray-600">{insumo.code || "-"}</td>
                <td className="p-3 text-gray-600">{insumo.category || "-"}</td>
                <td className="p-3 text-center">{insumo.quantity}</td>
                <td className="p-3 text-right">{formatPrice(insumo.cost_price || 0)}</td>
                <td className="p-3"><Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => deleteInsumo(insumo.id)}><Trash2 className="w-3.5 h-3.5" /></Button></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-gray-400">No se han encontrado insumo</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader><DialogTitle className="uppercase font-bold">Nuevo Insumo</DialogTitle></DialogHeader>
          <form onSubmit={createInsumo} className="space-y-3 mt-2">
            <div><Label>Nombre *</Label><Input value={name} onChange={e => setName(e.target.value)} required className="mt-1" data-testid="insumo-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Codigo</Label><Input value={code} onChange={e => setCode(e.target.value)} className="mt-1" /></div>
              <div><Label>Categoria</Label><Input value={category} onChange={e => setCategory(e.target.value)} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Cantidad</Label><Input type="number" step="0.01" value={quantity} onChange={e => setQuantity(e.target.value)} className="mt-1" /></div>
              <div><Label>Precio de costo</Label><Input type="number" step="0.01" value={costPrice} onChange={e => setCostPrice(e.target.value)} className="mt-1" /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-insumo-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
